/**
 * 登录页：家长登录 / 学生登录 / 注册家长 三个 Tab（学生没有注册入口）。
 *
 * 与后端契约对齐的关键点（见 auth.routes.ts + auth.service.ts）：
 *  - 请求体字段：role / username / password / captchaId / captchaAnswer / captchaFailures
 *                / totpCode / rememberDevice
 *  - 失败次数达到 CAPTCHA_AFTER_FAILURES（默认 1）后，后端会返回 CAPTCHA_REQUIRED，
 *    前端必须显示验证码输入框；失败次数存在本机 localStorage 并在请求里回传。
 *  - EMAIL_NOT_VERIFIED → 引导到邮箱验证页（带上邮箱）。
 *  - TOTP_REQUIRED → 显示动态口令输入框（家长开启了 2FA）。
 */
import { useCallback, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ERROR_CODES, checkPasswordStrength, homePathForRole } from '@fht/shared';
import { useAuth } from '../../lib/auth';
import * as authApi from '../../lib/api/auth';
import { ApiError } from '../../lib/api/error';
import { describeFailureMessage } from '../../lib/api/failure';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PasswordStrengthMeter } from '../../components/ui/PasswordStrengthMeter';
import { CaptchaField } from '../../components/CaptchaField';
import { BuildStamp } from '../../components/layout/BuildStamp';
import { cn } from '../../lib/utils';

type Tab = 'parent' | 'student' | 'register';

const TABS: Array<{ key: Tab; label: string; to: string }> = [
  { key: 'parent', label: '家长登录', to: '/login' },
  { key: 'student', label: '学生登录', to: '/login?tab=student' },
  { key: 'register', label: '注册家长', to: '/register' },
];

export function LoginPage() {
  const [params] = useSearchParams();
  const initialTab: Tab = params.get('tab') === 'student' ? 'student' : 'parent';
  const [tab, setTab] = useState<Tab>(initialTab);

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const role = tab === 'student' ? 'student' : 'parent';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [captchaId, setCaptchaId] = useState<string | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [totpCode, setTotpCode] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notVerifiedEmail, setNotVerifiedEmail] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failures, setFailures] = useState(() => authApi.getLoginFailures(role, username));

  /** 从邮箱验证页跳回来时的落点提示（验证通过后不自动登录，见方案 1.1 ④）。 */
  const justVerified = Boolean((location.state as { verified?: boolean } | null)?.verified);

  // 用户名变化时同步该账号的失败计数（localStorage 按 角色+用户名 存储）
  const onUsernameChange = (value: string) => {
    setUsername(value);
    setFailures(authApi.getLoginFailures(role, value));
  };

  const switchTab = (next: Tab) => {
    setTab(next);
    setError(null);
    setNotVerifiedEmail(null);
    setFailures(authApi.getLoginFailures(next === 'student' ? 'student' : 'parent', username));
  };

  const handleCaptchaChange = useCallback((id: string | null) => setCaptchaId(id), []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotVerifiedEmail(null);

    /**
     * 前端先做一次提示，减少无谓请求；真正的判定仍在后端。
     *
     * ⚠️ 空用户名 / 空密码**不发请求**：后端 `loginSchema` 会直接抛 zod，
     *    经 Fastify 默认错误处理器变成 500 + 一串 JSON（完整链路见
     *    `lib/api/failure.ts`）。这里只挡"根本没填"，**不**判断密码对不对 ——
     *    "用户名或密码不正确 / 账号锁定 / 要验证码"仍然走后端。
     */
    if (username.trim() === '') {
      setError('请填写用户名');
      return;
    }
    if (password === '') {
      setError('请填写密码');
      return;
    }

    // 验证码同样先说一遍
    if (captchaActive && (!captchaId || !captchaAnswer.trim())) {
      setError('请输入图形验证码');
      return;
    }

    setSubmitting(true);
    try {
      const payload: authApi.LoginPayload = {
        role,
        username,
        password,
        rememberDevice,
        captchaFailures: failures,
        ...(captchaActive && captchaId ? { captchaId } : {}),
        ...(captchaActive && captchaAnswer ? { captchaAnswer } : {}),
        ...(totpCode ? { totpCode } : {}),
      };
      const user = await login(payload);
      authApi.clearLoginFailures(role, username);

      const from = (location.state as { from?: string } | null)?.from;
      // 落点按角色决定：家长 → 首页、学生 → 今日打卡、系统管理员 → 管理后台
      // （管理员正常从 /admin/login 进，这里只是兜底，避免把他送进学生端）
      const home = homePathForRole(user.role);
      navigate(from && from !== '/login' ? from : home, { replace: true });
    } catch (err) {
      // 统一出口：任何失败都只显示人话（拿不到时「登录失败，请重试」），绝不显示 JSON。
      const message = describeFailureMessage(err, '登录失败，请重试');
      setError(message);

      const code = err instanceof ApiError ? err.code : null;

      if (code === ERROR_CODES.EMAIL_NOT_VERIFIED) {
        setNotVerifiedEmail(username.includes('@') ? username : null);
      }
      if (code === ERROR_CODES.TOTP_REQUIRED || code === ERROR_CODES.TOTP_INVALID) {
        setNeedTotp(true);
      }
      if (
        code === ERROR_CODES.INVALID_CREDENTIALS ||
        code === ERROR_CODES.CAPTCHA_REQUIRED ||
        code === ERROR_CODES.CAPTCHA_INVALID ||
        code === ERROR_CODES.CAPTCHA_EXPIRED
      ) {
        const next = authApi.recordLoginFailure(role, username);
        setFailures(next);
        setCaptchaAnswer('');
        setCaptchaNonce((n) => n + 1); // 换一张验证码，避免重复提交同一个
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 失败 ≥1 次就显示验证码（后端 CAPTCHA_AFTER_FAILURES 默认 1）
  const captchaActive = failures >= 1;

  // 注册 Tab 显示密码强度提示，登录 Tab 只在输入较长时提示一次弱密码
  const strengthHint = password
    ? checkPasswordStrength(password, { minLength: 1, identities: [username] })
    : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <span aria-hidden="true" className="text-4xl">
            📚
          </span>
          <h1 className="mt-2 text-2xl font-bold text-slate-900">家庭学习打卡</h1>
          <p className="mt-1 text-sm text-slate-500">和家人一起，把每天的小目标坚持下去</p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          {/* Tab 栏：注册是独立路由，用 Link 保持语义正确（真页面而不是伪 Tab） */}
          <div role="tablist" aria-label="登录方式" className="mb-5 grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1">
            {TABS.map((item) =>
              item.key === 'register' ? (
                <Link
                  key={item.key}
                  to={item.to}
                  role="tab"
                  aria-selected={false}
                  className="rounded-lg px-2 py-2 text-center text-sm font-medium text-slate-600 hover:bg-white"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === item.key}
                  onClick={() => switchTab(item.key)}
                  className={cn(
                    'rounded-lg px-2 py-2 text-sm font-medium transition-colors',
                    tab === item.key
                      ? 'bg-white text-brand-700 shadow-sm'
                      : 'text-slate-600 hover:bg-white/70',
                  )}
                >
                  {item.label}
                </button>
              ),
            )}
          </div>

          {justVerified && (
            <div
              role="status"
              className="mb-4 rounded-xl bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700"
            >
              邮箱验证成功，请用刚刚设置的密码登录。
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <Input
              label={role === 'student' ? '学生用户名' : '用户名'}
              name="username"
              autoComplete="username"
              value={username}
              onChange={(e) => onUsernameChange(e.target.value)}
              required
              autoFocus
            />

            <div className="space-y-1.5">
              <Input
                label="密码"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete={role === 'student' ? 'current-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                suffix={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="rounded-lg px-2 py-1.5 text-xs font-medium text-brand-600 hover:bg-brand-50"
                    aria-label={showPassword ? '隐藏密码' : '显示密码'}
                  >
                    {showPassword ? '隐藏' : '显示'}
                  </button>
                }
              />
              {/* 登录时不强求强度，仅在明显偏弱时给一句提示 */}
              {password.length > 0 && strengthHint && !strengthHint.ok && (
                <PasswordStrengthMeter password={password} />
              )}
            </div>

            {needTotp && (
              <Input
                label="动态口令"
                name="totpCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                hint="打开认证器 App，输入 6 位数字"
              />
            )}

            {captchaActive && (
              <div className="space-y-3 rounded-xl bg-amber-50/60 p-3">
                <p className="text-xs text-amber-800">
                  连续登录失败 {failures} 次，请输入图形验证码后重试。
                </p>
                <CaptchaField onChange={handleCaptchaChange} refreshToken={captchaNonce} />
                <Input
                  label="验证码"
                  name="captchaAnswer"
                  inputMode="text"
                  autoComplete="off"
                  maxLength={16}
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                />
              </div>
            )}

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              下次不用输密码（这台设备 30 天内免登录）
            </label>

            {error && (
              <div role="alert" className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
                {error}
                {notVerifiedEmail && (
                  <Link
                    to={`/register/verify?email=${encodeURIComponent(notVerifiedEmail)}`}
                    className="ml-1 font-semibold underline"
                  >
                    去完成邮箱验证
                  </Link>
                )}
              </div>
            )}

            <Button type="submit" fullWidth size="lg" loading={submitting}>
              {role === 'student' ? '进入学习' : '登录'}
            </Button>

            <div className="flex items-center justify-between text-sm">
              <Link to="/forgot" className="font-medium text-brand-600 hover:underline">
                忘记密码？
              </Link>
              <Link to="/register" className="font-medium text-brand-600 hover:underline">
                注册家长账号
              </Link>
            </div>
          </form>
        </div>

        <p className="mt-4 text-center text-xs text-slate-400">
          学生账号由家长创建，没有独立注册入口。
        </p>

        {/*
          「构建于 …」版本标记（2026-10-16 补）。

          为什么登录页也要有：未登录时**这一页就是家庭端唯一看得到的页面**
          —— 用户发来的"新 bug"截图十有八九是刷到的旧缓存，而强刷之后
          第一眼落在的就是这里；只在 AppShell 里显示的话，这一刻仍然无从确认。
          与管理端、家长端其它位置是**同一个** `BuildStamp`（见 lib/buildStamp.ts），
          值同源、格式同形；这里只是换个位置（居中卡片下方居中），不另写一份。
        */}
        <BuildStamp className="mt-4 text-center" />
      </div>
    </div>
  );
}
